/**
 * Footer Component
 * Professional footer for all pages
 */

import { Link } from "react-router-dom";
import { Sparkles, Twitter, Instagram, Youtube, Mail, Heart } from "lucide-react";

export function Footer({ minimal = false }) {
  const currentYear = new Date().getFullYear();

  if (minimal) {
    return (
      <footer className="border-t border-zinc-900 bg-black">
        <div className="max-w-6xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-zinc-600">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="ChariAI" className="w-4 h-4 rounded" />
            <span>© {currentYear} ChariAI. All rights reserved.</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/legal/terms" className="hover:text-zinc-400 transition-colors">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-zinc-400 transition-colors">Privacy</Link>
            <Link to="/pricing" className="hover:text-zinc-400 transition-colors">Pricing</Link>
            <a href="mailto:support@chariai.com" className="hover:text-zinc-400 transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="border-t border-zinc-900 bg-zinc-950">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Brand Column */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <img src="/logo.png" alt="ChariAI" className="w-8 h-8 rounded-lg" />
              <span className="font-bold text-lg text-white">ChariAI</span>
            </div>
            <p className="text-zinc-500 text-sm mb-4 leading-relaxed">
              Create scroll-stopping video scripts for TikTok, Instagram Reels, and YouTube Shorts in seconds.
            </p>
            <div className="flex items-center gap-3">
              <a 
                href="https://twitter.com/chariai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full bg-zinc-900 hover:bg-zinc-800 flex items-center justify-center text-zinc-500 hover:text-white transition-all"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a 
                href="https://instagram.com/chariai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full bg-zinc-900 hover:bg-zinc-800 flex items-center justify-center text-zinc-500 hover:text-white transition-all"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a 
                href="https://youtube.com/@chariai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full bg-zinc-900 hover:bg-zinc-800 flex items-center justify-center text-zinc-500 hover:text-white transition-all"
              >
                <Youtube className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Product Column */}
          <div>
            <h3 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Product</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-zinc-500 hover:text-white text-sm transition-colors">
                  Script Generator
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-zinc-500 hover:text-white text-sm transition-colors flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-violet-400" />
                  Pricing
                </Link>
              </li>
              <li>
                <Link to="/dashboard" className="text-zinc-500 hover:text-white text-sm transition-colors">
                  My Scripts
                </Link>
              </li>
            </ul>
          </div>

          {/* Platforms Column */}
          <div>
            <h3 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Platforms</h3>
            <ul className="space-y-3">
              <li>
                <span className="text-zinc-500 text-sm flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#25F4EE]"></span>
                  TikTok Scripts
                </span>
              </li>
              <li>
                <span className="text-zinc-500 text-sm flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-[#833AB4] to-[#FD1D1D]"></span>
                  Instagram Reels
                </span>
              </li>
              <li>
                <span className="text-zinc-500 text-sm flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#FF0000]"></span>
                  YouTube Shorts
                </span>
              </li>
            </ul>
          </div>

          {/* Support Column */}
          <div>
            <h3 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Support</h3>
            <ul className="space-y-3">
              <li>
                <a 
                  href="mailto:support@chariai.com" 
                  className="text-zinc-500 hover:text-white text-sm transition-colors flex items-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  support@chariai.com
                </a>
              </li>
              <li>
                <Link to="/settings" className="text-zinc-500 hover:text-white text-sm transition-colors">
                  Account Settings
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Legal Links Row */}
        <div className="pt-6 pb-4 border-t border-zinc-900">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-zinc-500">
            <Link to="/legal/terms" className="hover:text-zinc-300 transition-colors">Terms of Service</Link>
            <Link to="/legal/privacy" className="hover:text-zinc-300 transition-colors">Privacy Policy</Link>
            <Link to="/legal/ai-disclaimer" className="hover:text-zinc-300 transition-colors">AI Disclaimer</Link>
            <Link to="/legal/billing" className="hover:text-zinc-300 transition-colors">Billing Policy</Link>
            <Link to="/legal/acceptable-use" className="hover:text-zinc-300 transition-colors">Acceptable Use</Link>
            <Link to="/legal/copyright" className="hover:text-zinc-300 transition-colors">Copyright</Link>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-4 border-t border-zinc-900 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-1 text-zinc-600 text-xs">
            <span>© {currentYear} ChariAI. Made with</span>
            <Heart className="w-3 h-3 text-red-500 fill-red-500" />
            <span>for creators.</span>
          </div>
          <div className="flex items-center gap-6 text-xs">
            <Link to="/legal/privacy" className="text-zinc-600 hover:text-zinc-400 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/legal/terms" className="text-zinc-600 hover:text-zinc-400 transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
